import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import {
    AbstractControl,
    FormGroup,
    FormArray,
    FormBuilder,
    FormControl,
    ValidationErrors,
    ValidatorFn,
    Validators
} from '@angular/forms';
import { buildProviders, Coerce, KalSelectionModel } from '@kalidea/kaligraphi';
import { Store } from '@ngxs/store';
import cloneDeep from 'lodash-es/cloneDeep';
import { BehaviorSubject, merge, Observable, of, Subscription } from 'rxjs';
import { delay, distinctUntilChanged, filter, startWith, tap } from 'rxjs/operators';
import { CustomFormControlAccess } from 'src/app/_core/tools/custom-form-control-access';
import {
    SharedDaysInMonthEditComponent
} from 'src/app/_shared/components/shared-days-in-month-edit/shared-days-in-month-edit.component';
import { KalTableHeader } from 'src/app/_shared/modules/kal-table/classes/kal-table-header';
import {
    SharedAllocationAccessibilityLoadRelatedListAction
} from 'src/app/_shared/stores/allocation-accessibility/shared-allocation-accessibility.action';
import {
    SharedAllocationAccessibilityState
} from 'src/app/_shared/stores/allocation-accessibility/shared-allocation-accessibility.state';
import { arrayNotEmpty } from 'src/app/utils/array.utils';
import { filterListFromSelection } from 'src/app/utils/list.utils';
import { onFirst, wheneverEnd } from 'src/app/utils/operators.utils';
import { FormValidator } from 'src/app/validators/form-validator';
import { ChampDto, ModeleRegleAccModelDto, MoteurRegleTypeDateRefDto, TypeBenefDto } from 'src/graphql/generated-types';

type SharedAllocationAccessibilityEdit = {
    id?: string;
    IdValeurChamp?: number;
    Valeur?: string;
    AncienneteMinimum?: FormControl;
    AncienneteMaximum?: FormControl;
};

@Component({
    selector: 'app-shared-allocation-accessibility-edit',
    templateUrl: './shared-allocation-accessibility-edit.component.html',
    styleUrls: ['./shared-allocation-accessibility-edit.component.sass'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: buildProviders(SharedAllocationAccessibilityEditComponent),
    standalone: false
})
export class SharedAllocationAccessibilityEditComponent<T extends SharedAllocationAccessibilityEdit> extends CustomFormControlAccess implements OnInit {

    form: FormGroup;

    beneficiaryTypesList: TypeBenefDto[] = [];

    childrenAccessibilityCriteriaList: ChampDto[] = [];

    referenceDatesList: MoteurRegleTypeDateRefDto[] = [];

    accessibilityCriteriaList: ChampDto[] = [];

    columns: KalTableHeader<T>[] = [
        {label: 'Valeur', code: 'Valeur'},
        {label: 'Ancienneté Min en mois', code: 'AncienneteMinimum'},
        {label: 'Ancienneté Max en mois', code: 'AncienneteMaximum'},
    ];

    displayedAccessibilityCriteriaList: T[] = [];

    displayedChildrenAccessibilityCriteriaList: T[] = [];

    accessibilityCriteriaSelection = new KalSelectionModel<T>({multiple: true});

    childrenAccessibilityCriteriaSelection = new KalSelectionModel<T>({multiple: true});

    shouldDisplayedSelectedDate = false;

    shouldDisplayedSelectedChildrenDate = false;

    private subscriptionsList: Subscription[] = [];

    private accessibilityCriteriaSubscription: Subscription;

    private childrenAccessibilityCriteriaSubscription: Subscription;

    private isAccessibilityBeneficiary = true;

    constructor(private readonly cdr: ChangeDetectorRef,
                private readonly fb: FormBuilder,
                private readonly store: Store) {
        super();
    }

    private _isNominative$ = new BehaviorSubject(false);

    get isNominative$(): Observable<boolean> {
        return this._isNominative$.asObservable();
    }

    private _accessibilityRule = new BehaviorSubject<ModeleRegleAccModelDto>(null);

    get accessibilityRule(): ModeleRegleAccModelDto {
        return this._accessibilityRule.getValue();
    }

    set accessibilityRule(value: ModeleRegleAccModelDto) {
        this._accessibilityRule.next(value);
    }

    private _loading = false;

    get loading(): boolean {
        return this._loading;
    }

    set loading(loading: boolean) {
        this._loading = loading;
        this.cdr.markForCheck();
    }

    get isNominative(): boolean {
        return this._isNominative$.getValue();
    }

    @Coerce('boolean')
    @Input()
    set isNominative(value: boolean) {
        this._isNominative$.next(value);
    }

    get accessibilityRule$(): Observable<ModeleRegleAccModelDto> {
        return this._accessibilityRule.asObservable();
    }

    get displayedAccessibilityCriteriaColumns(): (keyof T)[] {
        return this.columns.filter(column => this.form.get('ModeAccess').value === 'ANC'
            || ['AncienneteMinimum', 'AncienneteMaximum'].every(e => e !== column.code)).map(e => e.code);
    }

    get displayedChildrenAccessibilityCriteriaColumns(): (keyof T)[] {
        return ['Valeur'];
    }

    get listeTypesBeneficiaire(): FormArray {
        return this.form.get('ListeTypesBeneficiaire') as FormArray;
    }

    get hasAgeValue(): boolean {
        return this.form.get('AgeEnfantSelection').value;
    }

    get displayedAccessibilityBeneficiaryContent(): boolean {
        return [
            ['ModeAccess', 'ANC'],
            ['TypeAccessibilite', 'accessibilityBeneficiary']
        ].every(([control, value]) => this.form.get(control).value === value);
    }

    setDisabledState(isDisabled: boolean): void {
        if (isDisabled) {
            this.form.disable({emitEvent: false});
        } else {
            this.form.enable({emitEvent: false});
        }
    }

    validate(c: AbstractControl): Observable<ValidationErrors> {
        const fieldId = this.form.get('IdChamp').value;
        const hasAtLeastOneFieldIfFieldId = !this.isAccessibilityBeneficiary ? !fieldId || !this.accessibilityCriteriaSelection.isEmpty() : true;

        const isValid = this.form.valid
            && hasAtLeastOneFieldIfFieldId;

        return of(isValid ? null : {invalidAccessibilityRules: true});
    }

    writeValue(value: ModeleRegleAccModelDto): void {
        this.accessibilityRule = value;
        super.writeValue(value);
    }

    shouldDisplayedBeneficiary(types: string[]): boolean {
        return this.isNominative && types.some(e => {
            return this.listeTypesBeneficiaire.value.some(({Code, Valeur}) => Code === e && Valeur);
        });
    }

    getBeneficiaryLabel(index: number): string {
        return this.listeTypesBeneficiaire.controls[index].value?.Libelle;
    }

    stopPropagation($event): void {
        $event.stopPropagation();
    }

    private normalize(allocation = this.form.value): ModeleRegleAccModelDto {
        // const allocation = this.form.value;

        const accessibilityCriteriaList = {
            displayedAccessibilityCriteriaList: 'accessibilityCriteriaSelection',
            displayedChildrenAccessibilityCriteriaList: 'childrenAccessibilityCriteriaSelection'
        };

        const [ListeModeleRegleAccListeModele, ListeIdChampAD] = Object.keys(accessibilityCriteriaList).map(key => {
            return filterListFromSelection(this[key], this[accessibilityCriteriaList[key]], 'id') as T[];
        });

        const ListeIdTypeBenef = arrayNotEmpty((allocation.ListeTypesBeneficiaire ?? []).filter(e => e.Valeur))
            ? {decimal: allocation.ListeTypesBeneficiaire.filter(e => e.Valeur).map(e => e.Id)}
            : null;

        const accessibilityRulesList = arrayNotEmpty(ListeModeleRegleAccListeModele)
            ? {
                RegleAccListeValModelDTO: ListeModeleRegleAccListeModele.map(e => {
                    return {
                        IdValeurChamp: e.IdValeurChamp,
                        Valeur: e.Valeur,
                        AncienneteMinimum: e.AncienneteMinimum?.value || null,
                        AncienneteMaximum: e.AncienneteMaximum?.value || null
                    };
                })
            }
            : null;

        const isChildrenDisplayed = this.shouldDisplayedBeneficiary(['ENF']);

        const familyCriteriaList = arrayNotEmpty(ListeIdChampAD) && isChildrenDisplayed
            ? {decimal: ListeIdChampAD.map(e => e.IdValeurChamp)}
            : null;

        const normalizeChildrenProperty = (propertyValue) => {
            return isChildrenDisplayed ? (propertyValue || null) : null;
        };

        const normalizeEntryDates =
            (dateId: number, entryDates: { day: number, month: number }, property: 'day' | 'month'): null | number => {
                if (!entryDates) {
                    return null;
                }

                const referenceDate = this.referenceDatesList.find(d => d.Id === dateId);
                return (['SAI', 'SAIINF'].includes(referenceDate?.Code))
                    ? entryDates[property]
                    : null;
            };

        return {
            Id: +allocation.Id || 0,
            ListeIdTypeBenef,
            ModeAccess: allocation.ModeAccess,
            IdtypeRefAnc: +allocation.IdtypeRefAnc || null,
            DateRefJour: normalizeEntryDates(allocation.IdtypeRefAnc, allocation.DateRef, 'day'),
            DateRefMois: normalizeEntryDates(allocation.IdtypeRefAnc, allocation.DateRef, 'month'),
            RgModAccesAnMin: +allocation.RgModAccesAnMin || null,
            RgModAccesAnMax: +allocation.RgModAccesAnMax || null,
            IdChamp: +allocation.IdChamp || null,
            ListeModeleRegleAccListeModele: accessibilityRulesList,
            IsConjointHandicape: this.shouldDisplayedBeneficiary(['CON']) && allocation.IsConjointHandicape,
            IsEnfantDirect: isChildrenDisplayed && allocation.IsEnfantDirect,
            IsEnfantHandicape: isChildrenDisplayed && allocation.IsEnfantHandicape,
            IsEnfantACharge: isChildrenDisplayed && allocation.IsEnfantACharge,
            AgeEnfantMin: normalizeChildrenProperty(+allocation.AgeEnfantMin),
            AgeEnfantMax: normalizeChildrenProperty(+allocation.AgeEnfantMax),
            IdTypeDateRefAge: normalizeChildrenProperty(+allocation.IdTypeDateRefAge),
            DateRefJourAgeEnf: normalizeEntryDates(allocation.IdTypeDateRefAge, normalizeChildrenProperty(allocation.DateRefEnf), 'day'),
            DateRefMoisAgeEnf: normalizeEntryDates(allocation.IdTypeDateRefAge, normalizeChildrenProperty(allocation.DateRefEnf), 'month'),
            IdChampAD: normalizeChildrenProperty(+allocation.IdChampAD),
            ListeIdChampAD: familyCriteriaList
        };
    }

    private loadRelatedList(): void {
        this.loading = true;

        this.watch$ = this.store.dispatch(new SharedAllocationAccessibilityLoadRelatedListAction()).pipe(
            tap(() => {
                this.accessibilityCriteriaList = this.store.selectSnapshot(SharedAllocationAccessibilityState.accessibilityCriteriaList);
                this.referenceDatesList = this.store.selectSnapshot(SharedAllocationAccessibilityState.referenceDatesList);
                this.beneficiaryTypesList = cloneDeep(this.store.selectSnapshot(SharedAllocationAccessibilityState.beneficiaryTypesList))?.sort((a) => a.Code !== 'OD' ? 1 : -1) || [];
                this.childrenAccessibilityCriteriaList = this.store.selectSnapshot(SharedAllocationAccessibilityState.childrenAccessibilityCriteriaList);

                this.watch$ = this.accessibilityRule$.pipe(
                    delay(1),
                    tap(() => {
                        this.updateForm();
                    })
                );
            }),
            wheneverEnd(() => this.loading = false)
        );
    }

    private updateForm(): void {
        this.subscriptionsList.forEach(subscription => {
            subscription?.unsubscribe();
        });
        // On stocke l'état initial pour le réappliquer si il est perdu.
        const disabled = this.form.disabled;

        const {
            MustValidate,
            MyGuid,
            Status,
            AncienneteFactureMax,
            IsForcageAutorise,
            IsNominative,
            ListeIdChampAD,
            ListeIdTypeBenef,
            ListeModeleRegleAccListeModele,
            DateRefJour,
            DateRefMois,
            DateRefJourAgeEnf,
            DateRefMoisAgeEnf,
            IdChamp,
            IdtypeRefAnc,
            ...ruleRest
        } = this.accessibilityRule || {};

        this.form.patchValue({
            ...ruleRest,
            Id: this.accessibilityRule?.Id || null,
            AgeEnfantSelection: ['AgeEnfantMin', 'AgeEnfantMax'].some(e => this.accessibilityRule?.[e]),
            DateRef: {
                day: DateRefJour,
                month: DateRefMois
            },
            DateRefEnf: {
                day: DateRefJourAgeEnf,
                month: DateRefMoisAgeEnf
            },
            IdtypeRefAnc: +IdtypeRefAnc || null,
            IdChamp: +IdChamp || null,
            TypeAccessibilite: !this.accessibilityRule?.IdChamp ? 'accessibilityBeneficiary' : 'accessibilityCriteria',
        }, {emitEvent: false});

        this.listeTypesBeneficiaire.clear({emitEvent: false});

        this.beneficiaryTypesList.forEach(e => {
            this.listeTypesBeneficiaire.push(this.fb.group({
                Id: e.Id,
                Libelle: e.Libelle,
                Code: e.Code,
                Valeur: this.accessibilityRule?.ListeIdTypeBenef?.decimal?.includes(e.Id)
            }), {emitEvent: false});
        });

        if (this.listeTypesBeneficiaire.controls.every(c => c.get('Valeur').value !== true)) {
            this.listeTypesBeneficiaire.controls.find(c => c.get('Code').value === 'OD').patchValue({Valeur: true}, {emitEvent: false});
        }

        this.form[disabled ? 'disable' : 'enable']({emitEvent: false});

        if (this.accessibilityRule?.ModeAccess === 'ANC' && !IdtypeRefAnc) {
            // Défini la date de référence par défaut à la date du jour si aucune n'est renseignée
            const today = this.referenceDatesList.find(d => d.Code === 'NOW');
            this.form.get('IdtypeRefAnc').patchValue(today?.Id, {emitEvent: false});
        }

        // Active les contrôles si l'age existe
        const hasChildrenAges = !!this.accessibilityRule?.AgeEnfantMin || !!this.accessibilityRule?.AgeEnfantMax;
        this.form.get('AgeEnfantSelection').patchValue(hasChildrenAges, {emitEvent: false});

        this.initSubscription();
        this.cdr.markForCheck();
    }

    private isMinMaxSeniority(id: number): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const {
                AncienneteMinimum,
                AncienneteMaximum
            } = this.displayedAccessibilityCriteriaList.find(e => e.id === '' + id) || {};
            const oppositeControl = AncienneteMinimum === control ? AncienneteMaximum : AncienneteMinimum;
            oppositeControl?.setErrors(null);
            if (!AncienneteMinimum?.value || !AncienneteMaximum?.value || AncienneteMinimum.value < AncienneteMaximum.value) {
                return null;
            }
            return {notMinMaxSeniority: true};
        };
    }

    private initSubscription(): void {
        const {
            IdChamp: fieldIdControl,
            IdChampAD: adFieldIdControl,
            TypeAccessibilite: accessibilityTypeControl,
            DateRef: referenceDateControl,
            DateRefEnf: childrenReferenceDateControl,
            ModeAccess: accessibilityModeControl
        } = this.form.controls;

        this.subscriptionsList.push(
            fieldIdControl.valueChanges.pipe(
                this.untilDestroyed,
                distinctUntilChanged(),
                startWith(fieldIdControl.value),
                tap(value => {
                    this.refreshCriteriaList(value);
                }),
                onFirst(() => {
                    const values = this.displayedAccessibilityCriteriaList.filter(e => {
                        return this.accessibilityRule?.ListeModeleRegleAccListeModele?.RegleAccListeValModelDTO?.some(({IdValeurChamp}) => +IdValeurChamp === +e.IdValeurChamp);
                    });

                    if (arrayNotEmpty(values)) {
                        this.accessibilityCriteriaSelection.select(...values);
                    }
                }),
                tap(() => {
                    this.initCriteriaSelectionValueChanges();
                })
            ).subscribe()
        );

        this.subscriptionsList.push(
            adFieldIdControl.valueChanges.pipe(
                this.untilDestroyed,
                distinctUntilChanged(),
                startWith(adFieldIdControl.value),
                tap(value => {
                    this.childrenAccessibilityCriteriaSubscription?.unsubscribe();

                    this.displayedChildrenAccessibilityCriteriaList = (this.childrenAccessibilityCriteriaList
                        .find(e => e.Id === value)?.ListChampListeValeur?.ChampListeValeurDTO || [])
                        .filter(e => !e.IsCloture)
                        .map(e => {
                            return {
                                IdValeurChamp: e.Id,
                                Valeur: e.Valeur,
                            };
                        }) as T[] || [];

                    const selectionsList = this.displayedChildrenAccessibilityCriteriaList?.filter(e => {
                        return this.accessibilityRule?.ListeIdChampAD?.decimal.some(id => id === e.IdValeurChamp);
                    });
                    this.childrenAccessibilityCriteriaSelection.select({values: selectionsList, emitEvent: false});

                    this.childrenAccessibilityCriteriaSubscription = this.childrenAccessibilityCriteriaSelection.changes.pipe(
                        this.untilDestroyed,
                        tap(() => this.notifyUpdate(this.normalize()))
                    ).subscribe();

                    this.cdr.markForCheck();
                }),
            ).subscribe()
        );

        const accessibilityDates = {
            IdtypeRefAnc: {
                displayedSelectedDateFields: 'shouldDisplayedSelectedDate',
                dateControl: referenceDateControl
            },
            IdTypeDateRefAge: {
                displayedSelectedDateFields: 'shouldDisplayedSelectedChildrenDate',
                dateControl: childrenReferenceDateControl
            }
        };

        this.subscriptionsList.push(
            merge(
                ...Object.keys(accessibilityDates).map(key => {
                    return this.form.get(key).valueChanges.pipe(
                        distinctUntilChanged(),
                        startWith(this.form.get(key).value),
                        tap(value => {
                            const control = accessibilityDates[key].dateControl;
                            this[accessibilityDates[key].displayedSelectedDateFields] = ['SAI', 'SAIINF'].some(code => {
                                return code === this.referenceDatesList.find(({Id}) => Id === value)?.Code;
                            });

                            if (this[accessibilityDates[key].displayedSelectedDateFields]) {
                                control.setValidators([SharedDaysInMonthEditComponent.validator]);
                            } else {
                                // Reset du control lorsqu'il n'y a pas de valeur à saisir
                                control.clearValidators();
                                control.reset({
                                    day: 1,
                                    month: 1
                                }, {emitEvent: false});
                            }

                            this.cdr.markForCheck();
                        })
                    );
                })
            ).pipe(
                this.untilDestroyed
            ).subscribe()
        );

        this.subscriptionsList.push(
            accessibilityTypeControl.valueChanges.pipe(
                startWith(accessibilityTypeControl.value),
                this.untilDestroyed,
                tap(value => {
                    this.isAccessibilityBeneficiary = value === 'accessibilityBeneficiary';
                    ['RgModAccesAnMin', 'RgModAccesAnMax'].forEach(e => {
                        if (this.isAccessibilityBeneficiary && this.form.enabled) {
                            this.form.get(e).enable({emitEvent: false});
                        } else {
                            this.form.get(e).disable({emitEvent: false});
                        }
                    });

                    if (!this.isAccessibilityBeneficiary && !fieldIdControl.value) {
                        const defaultId = this.accessibilityCriteriaList?.[0].Id || null;
                        fieldIdControl.patchValue(defaultId, {emitEvent: false});
                        this.refreshCriteriaList(defaultId);
                        this.initCriteriaSelectionValueChanges();
                    }

                    fieldIdControl[this.isAccessibilityBeneficiary ? 'disable' : 'enable']({emitEvent: false});
                })
            ).subscribe()
        );

        this.subscriptionsList.push(
            accessibilityModeControl.valueChanges.pipe(
                this.untilDestroyed,
                // si on passe de "Ancienneté" à "Date de présence", il faut réinitialiser les critères d'ancienneté
                // sinon, les valeurs sont quand-même enregistré en base et faussent ainsi le calcul de l'attribution
                filter(value => value === 'PRS'),
                tap(() =>
                    [
                        'RgModAccesAnMin',
                        'RgModAccesAnMax'
                    ].forEach(formControlName => this.form.get(formControlName).reset(null, {emitEvent: false}))
                )
            ).subscribe()
        );

        this.subscriptionsList.push(
            this.form.valueChanges.pipe(
                this.untilDestroyed,
                startWith(null),
                tap(() => this.notifyUpdate(this.normalize()))
            ).subscribe()
        );

        this.subscriptionsList.push(
            this.form.get('AgeEnfantSelection').valueChanges.pipe(
                this.untilDestroyed,
                tap(handleAge => {
                    ['AgeEnfantMin', 'AgeEnfantMax'].forEach(k => this.form.get(k)[handleAge ? 'enable' : 'disable']({emitEvent: false}));
                    if (!handleAge) {
                        this.form.patchValue({
                            AgeEnfantMin: null,
                            AgeEnfantMax: null
                        }, {emitEvent: false});
                    }
                })
            ).subscribe()
        );

        this.subscriptionsList.push(
            this.isNominative$.pipe(
                this.untilDestroyed,
                tap(value => {
                    if (value) {
                        this.listeTypesBeneficiaire.setValidators(this.hasAtLeastOnebeneficiariesTypeValidator);
                    } else {
                        this.listeTypesBeneficiaire.clearValidators();
                    }
                    this.listeTypesBeneficiaire.updateValueAndValidity({emitEvent: false});

                    // J'utilise un set timeout sinon le formulaire n'a pas le temps de détecter qu'il ne soit plus forcément valide
                    setTimeout(() => this.cdr.markForCheck());
                })
            ).subscribe()
        );
    }

    private initCriteriaSelectionValueChanges(): void {
        this.accessibilityCriteriaSubscription = merge(
            ...this.displayedAccessibilityCriteriaList.flatMap(e => [e.AncienneteMinimum.valueChanges, e.AncienneteMaximum.valueChanges]),
            this.accessibilityCriteriaSelection.changes
        ).pipe(
            this.untilDestroyed,
            tap(() => this.notifyUpdate(this.normalize()))
        ).subscribe();
    }

    private refreshCriteriaList(value: number): void {
        // @todo Parfois ce code est exécuté sans que la souscription soit régenérée derrière
        this.accessibilityCriteriaSubscription?.unsubscribe();
        this.accessibilityCriteriaSelection.clear(false);

        this.displayedAccessibilityCriteriaList = this.accessibilityCriteriaList.find(e => e.Id === value)?.ListChampListeValeur?.ChampListeValeurDTO
            ?.filter(e => !e.IsCloture)
            .map(e => {
                const selectedCriterion = this.accessibilityRule?.ListeModeleRegleAccListeModele?.RegleAccListeValModelDTO?.find(({IdValeurChamp}) => {
                    return IdValeurChamp === e.Id;
                });

                return {
                    id: '' + e.Id,
                    IdValeurChamp: e.Id,
                    Valeur: e.Valeur,
                    AncienneteMinimum: new FormControl(selectedCriterion?.AncienneteMinimum, {
                        validators: [FormValidator.int, this.isMinMaxSeniority(e.Id)],
                        updateOn: 'blur'
                    }),
                    AncienneteMaximum: new FormControl(selectedCriterion?.AncienneteMaximum, {
                        validators: [FormValidator.int, this.isMinMaxSeniority(e.Id)],
                        updateOn: 'blur'
                    })
                };
            }) as T[] || [];

        this.accessibilityCriteriaSelection.numberOfItems = this.displayedAccessibilityCriteriaList.length;
        this.cdr.markForCheck();
    }

    private hasAtLeastOnebeneficiariesTypeValidator(formArray: FormArray): { nobeneficiarytypes: boolean } | null {
        return formArray.controls.every(c => c.get('Valeur').value !== true)
            ? {nobeneficiarytypes: true}
            : null;
    }

    ngOnInit(): void {
        this.form = this.fb.group({
            Id: [0],
            ListeTypesBeneficiaire: this.fb.array([]),
            IdtypeRefAnc: [''],
            ModeAccess: [''],
            DateRef: [''],
            RgModAccesAnMin: ['', FormValidator.int],
            RgModAccesAnMax: ['', FormValidator.int],
            TypeAccessibilite: [''],
            IdChamp: [''],
            IsConjointHandicape: [false],
            IsEnfantDirect: [false],
            IsEnfantHandicape: [false],
            IsEnfantACharge: [false],
            AgeEnfantSelection: [''],
            IsAccessibiliteEnfant: [false],
            IsAccessibiliteConjoint: [false],
            IsAccessibiliteExterne: [false],
            IsAccessibiliteOuvrantDroit: [false],
            AgeEnfantMin: [{value: null, disabled: true}, [Validators.min(0)]],
            AgeEnfantMax: [{value: null, disabled: true}, [Validators.min(0)]],
            IdTypeDateRefAge: [''],
            IdChampAD: [null],
            DateRefEnf: [null],
            __typename: ['']
        }, {
            validators: [
                form => (!!form.value.AgeEnfantSelection
                    && !!form.value.AgeEnfantMin && !!form.value.AgeEnfantMax
                    && form.value.AgeEnfantMin > form.value.AgeEnfantMax ? {ageminsupmax: true} : null
                ),
                form => (!!form.value.AgeEnfantSelection && !form.value.AgeEnfantMin && !form.value.AgeEnfantMax ? {required: true} : null),
                form => (!!form.value.RgModAccesAnMin && !!form.value.RgModAccesAnMax
                    && form.value.RgModAccesAnMax <= form.value.RgModAccesAnMin ? {rgModAccesAnminsupmax: true} : null
                ),
            ]
        });

        this.loadRelatedList();
    }

}
